import {
  Anchor,
  Box,
  Card,
  Container,
  Group,
  SimpleGrid,
  Stack,
  Text,
  useComputedColorScheme,
} from '@mantine/core';

import { MarketingEyebrow } from '@/components/atoms';
import { useInView } from '@/hooks/useInView';
import { T } from '@/theme/assay';

const FAZIER_LAUNCH_URL = 'https://fazier.com/launches/assay';

type Quote = {
  body: string;
  name: string;
  role: string;
};

// Public comments from the Fazier launch, quoted verbatim with attribution and a
// link back to the source. Not solicited testimonials - if we ever get those,
// they go somewhere clearly separate from these.
const LAUNCH_QUOTES: Quote[] = [
  {
    body: 'The "after a job change" use case is the one that got me. You switch companies and 500 relationships start decaying silently, and no deal-focused CRM treats that as a problem worth solving.',
    name: 'Mario S',
    role: 'Solo founder',
  },
  {
    body: 'Assay\'s proactive reminders shift you from guilt-driven "I should reach out" to actually staying connected before the urgency hits.',
    name: 'Gal D',
    role: 'Scout',
  },
];

function QuoteCard({ quote, order, inView }: { quote: Quote; order: number; inView: boolean }) {
  return (
    <Card
      component="figure"
      withBorder
      radius="sm"
      padding="lg"
      m={0}
      style={{
        backgroundColor: 'var(--assay-surface)',
        opacity: 0,
        animation: inView
          ? `assay-word-rise 0.55s cubic-bezier(0.22, 1, 0.36, 1) ${0.5 + order * 0.1}s both`
          : undefined,
      }}
    >
      <Stack gap="md">
        <Text
          component="blockquote"
          m={0}
          style={{
            fontFamily: '"Cormorant Garamond", serif',
            fontWeight: 300,
            fontStyle: 'italic',
            fontSize: '1.2rem',
            lineHeight: 1.5,
            color: T.textPrimary,
          }}
        >
          {quote.body}
        </Text>
        <Text
          component="figcaption"
          style={{
            fontFamily: '"DM Mono", monospace',
            fontSize: '0.65rem',
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: T.textMuted,
          }}
        >
          <Box component="span" style={{ color: T.oreLight }}>
            {quote.name}
          </Box>
          {' - '}
          {quote.role}
        </Text>
      </Stack>
    </Card>
  );
}

export function LaunchProofSection() {
  const { ref, inView } = useInView(0.1);
  // Computed, not set: `useMantineColorScheme` returns 'auto' for most visitors
  // (App mounts with defaultColorScheme="auto"), which would serve the light
  // badge to anyone on a dark OS.
  const colorScheme = useComputedColorScheme('dark');

  return (
    <Box ref={ref} component="section" aria-labelledby="launch-proof-heading">
      <Container size="lg">
        <Stack gap={32}>
          <Group justify="space-between" align="flex-end" wrap="wrap" gap="md">
            <Stack gap={16} maw={620}>
              <MarketingEyebrow label="From the launch" inView={inView} />
              <Box
                component="h2"
                id="launch-proof-heading"
                style={{
                  fontFamily: '"Cormorant Garamond", serif',
                  fontWeight: 300,
                  fontSize: 'clamp(1.9rem, 3.4vw, 2.75rem)',
                  lineHeight: 1.15,
                  letterSpacing: '-0.02em',
                  color: T.textPrimary,
                  margin: 0,
                  opacity: 0,
                  animation: inView ? 'assay-word-rise 0.6s ease 0.45s both' : undefined,
                }}
              >
                What people said.
              </Box>
            </Stack>

            <Anchor
              href={FAZIER_LAUNCH_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                opacity: 0,
                animation: inView ? 'assay-fade-in 0.6s ease 0.6s both' : undefined,
              }}
            >
              <Box
                component="img"
                src={`https://fazier.com/api/v1/public/badges/embed_image.svg?launch_id=11166&badge_type=featured&theme=${colorScheme === 'dark' ? 'dark' : 'neutral'}`}
                alt="Assay featured on Fazier"
                width={230}
                style={{ display: 'block', maxWidth: '100%' }}
              />
            </Anchor>
          </Group>

          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
            {LAUNCH_QUOTES.map((quote, i) => (
              <QuoteCard key={quote.name} quote={quote} order={i} inView={inView} />
            ))}
          </SimpleGrid>

          <Text size="xs" c="dimmed">
            Using Assay and happy to be quoted? Email hello@runassay.app.
          </Text>
        </Stack>
      </Container>
    </Box>
  );
}
